import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faClock } from '@fortawesome/pro-regular-svg-icons';
import { isFuture } from 'date-fns';
import type { ReactNode } from 'react';
import { FormattedTime } from 'react-intl';

import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { Tooltip } from '@sorare/core/src/atoms/tooltip/Tooltip';
import { LabelM } from '@sorare/core/src/atoms/typography';
import { TimeLeft } from '@sorare/core/src/contexts/ticker/TimeLeft';

import type { PackRotationAt_cardPackGroup } from './__generated__/index.graphql';

type Props = {
  cardPackGroup: Nullable<PackRotationAt_cardPackGroup>;
  children?: (timeLeft: ReactNode) => ReactNode;
};

export const PackRotationAt = ({ cardPackGroup, children }: Props) => {
  const { nextPackRotationAt } = cardPackGroup || {};
  if (!nextPackRotationAt || !isFuture(nextPackRotationAt)) {
    return null;
  }
  return (
    <Horizontal gap={0.5}>
      <FontAwesomeIcon icon={faClock} color="var(--c-yellow-400)" size="xs" />
      <LabelM bold color="var(--c-yellow-400)">
        <Tooltip
          title={
            <FormattedTime
              dateStyle="short"
              day="numeric"
              month="short"
              year="numeric"
              value={nextPackRotationAt}
            />
          }
        >
          {children ? (
            children(<TimeLeft time={nextPackRotationAt} />)
          ) : (
            <TimeLeft time={nextPackRotationAt} />
          )}
        </Tooltip>
      </LabelM>
    </Horizontal>
  );
};

PackRotationAt.fragments = {
  cardPackGroup: gql`
    fragment PackRotationAt_cardPackGroup on CardPackGroup {
      id
      nextPackRotationAt
    }
  ` as TypedDocumentNode<PackRotationAt_cardPackGroup>,
};
